import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { SerializedDirectedVideo } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../../common/types';
import { priceMessages as messages } from '../../messages/priceMessages';
import { makeTiles, type ProgressTileState } from './progressRules';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the price and the make; each part uses its own slice of it */
import styles from './Make.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProgressTilesProps {
  video: Pick<SerializedDirectedVideo, 'status' | 'progress' | 'clips' | 'clipEntries'>;
}

/**
 * One tile for each Veo clip of the make (design g1, g2), numbered in clip order. Each state looks
 * different, so a clip in the making never looks ready:
 * - ready: the still of the clip at full strength;
 * - making: the keyframe of the clip (the first frame that Veo animates) dimmed, under the shimmer,
 *   with a small spinner;
 * - queued: the dashed empty tile;
 * - failed: a red edge and the "failed" mark.
 * A tile never shows the cover art of the song.
 */
const ProgressTilesComponent: React.FC<ProgressTilesProps> = ({ video }) => {
  const intl = useIntl();
  const tiles = useMemo(() => makeTiles(video), [video]);
  const titleOf = (state: ProgressTileState, number: number): string | undefined => {
    if (state === 'failed') {
      return intl.formatMessage(messages.tileFailedNote, { number });
    }
    if (state === 'making') {
      return intl.formatMessage(messages.tileMakingNote, { number });
    }
    return undefined;
  };
  return (
    <ul className={styles.tiles} aria-label={intl.formatMessage(messages.tilesLabel)}>
      {tiles.map((tile, index) => {
        const number = index + 1;
        return (
          <li
            key={tile.key}
            className={clsx(
              styles.tile,
              tile.state === 'making' && styles.tileMaking,
              tile.state === 'queued' && styles.tileQueued,
              tile.state === 'failed' && styles.tileFailed
            )}
            data-state={tile.state}
            aria-label={intl.formatMessage(messages.tileShot, { number })}
            title={titleOf(tile.state, number)}
          >
            {tile.stillUrl !== null && <img src={tile.stillUrl} alt="" loading="lazy" />}
            {tile.state === 'making' && <div className={shell.shimmer} />}
            {tile.state === 'making' && <span className={styles.tileSpinner} aria-hidden />}
            <span className={styles.tileNumber}>{number}</span>
            {tile.state === 'failed' && (
              <span className={styles.tileTag}>{intl.formatMessage(messages.tileFailed)}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export const ProgressTiles = TypedMemo(ProgressTilesComponent);
